import type { ReactNode } from "react";

type Tone = "dark" | "panel" | "lime" | "lime-soft" | "lime-panel";
type Size = "lg" | "md" | "sm";

const TONE_CLASS: Record<Tone, string> = {
  dark: "bg-[#050706] border-t border-white/[0.07]",
  panel: "bg-[#080b09] border-t border-white/[0.07]",
  lime: "bg-lime-300 text-black pr-halftone",
  /** Ciemne tło z delikatną limonkową poświatą - łagodzi długie czarne pasma. */
  "lime-soft": "bg-[#060907] border-t border-lime-300/20",
  /** Panel z wyraźniejszym limonkowym refleksem. */
  "lime-panel": "bg-[#070b07] border-t border-lime-300/18",
};

/** Miękkie, wspólne światło: jedna rodzina gradientów dla wszystkich ciemnych pasm. */
const TONE_GLOW: Partial<Record<Tone, string>> = {
  dark:
    "radial-gradient(140% 100% at 50% -10%, rgba(190,242,100,0.045) 0%, rgba(190,242,100,0) 55%), linear-gradient(180deg, rgba(255,255,255,0.022) 0%, rgba(255,255,255,0) 32%)",
  panel:
    "radial-gradient(120% 90% at 15% -5%, rgba(190,242,100,0.05) 0%, rgba(190,242,100,0) 58%), linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 38%)",
  "lime-soft":
    "radial-gradient(120% 85% at 12% 0%, rgba(190,242,100,0.11) 0%, rgba(190,242,100,0) 62%), linear-gradient(180deg, rgba(190,242,100,0.05) 0%, rgba(190,242,100,0) 45%)",
  "lime-panel":
    "radial-gradient(100% 90% at 88% 8%, rgba(190,242,100,0.10) 0%, rgba(190,242,100,0) 64%), linear-gradient(180deg, rgba(190,242,100,0.04) 0%, rgba(190,242,100,0) 42%)",
};


const SIZE_CLASS: Record<Size, string> = {
  lg: "py-14 sm:py-20 md:py-36",
  md: "py-12 sm:py-16 md:py-28",
  sm: "py-10 sm:py-12 md:py-20",
};

export interface SectionShellProps {
  id?: string;
  tone?: Tone;
  size?: Size;
  className?: string;
  innerClassName?: string;
  children: ReactNode;
  /** Optional absolutely positioned background layers rendered behind content. */
  backdrop?: ReactNode;
  sectionRef?: React.Ref<HTMLElement>;
}

/**
 * Wspólna powłoka sekcji: jednolita siatka bazowa, rytm pionowy i warianty tła.
 */
export function SectionShell({
  id,
  tone = "dark",
  size = "lg",
  className = "",
  innerClassName = "",
  children,
  backdrop,
  sectionRef,
}: SectionShellProps) {
  const isLime = tone === "lime";

  return (
    <section id={id} ref={sectionRef} className={`relative scroll-mt-20 md:scroll-mt-24 ${TONE_CLASS[tone]} ${className}`}>
      {!isLime && <div aria-hidden className="pointer-events-none absolute inset-0 pr-grid-bg" />}
      {TONE_GLOW[tone] && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: TONE_GLOW[tone] }}
        />
      )}
      {!isLime && (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, rgba(190,242,100,0) 0%, rgba(190,242,100,0.45) 22%, rgba(190,242,100,0.08) 55%, rgba(190,242,100,0) 100%)",
            }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 h-px w-1/3 anim-pr-hairline-sweep"
            style={{
              background:
                "linear-gradient(90deg, rgba(190,242,100,0) 0%, rgba(190,242,100,0.9) 50%, rgba(190,242,100,0) 100%)",
            }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24 pr-section-fade"
          />
        </>
      )}
      {backdrop}

      <div
        className={`relative z-10 max-w-[1280px] mx-auto px-5 sm:px-6 md:px-10 ${SIZE_CLASS[size]} ${innerClassName}`}
      >
        {children}
      </div>
    </section>
  );
}


/** Standardowy odstęp między nagłówkiem sekcji a jej treścią. */
export function SectionBody({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`mt-8 sm:mt-10 md:mt-20 ${className}`}>{children}</div>;
}
